import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fragment, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { type ConfigFilter, type ConfigFilterOther } from '../../../_configs/types';
import type { ConfigFilterPrefixes } from '../../../_tools/utils';
import CarListSort from '../../../components/car-list-sort';
import ChipFilters from '../../../components/chips-filter';
import RHFTextField from '../../../components/hook-form/rhf-text-field/component';
import Iconify from '../../../components/iconify/component';
import { useRootContainerContext } from '../../../context/root-container';
import { useSettingsContext } from '../../../context/settings';
import { type UseCarSearchReturn } from '../../../hooks/use-car-search/types';
import { useResponsive } from '../../../hooks/use-responsive';
import useLocales from '../../../hooks/useLocales';
import checkUndefined from '../../../utils/tools/checkUndefined';
import checkUndefinedNullOrEmpty from '../../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../../utils/tools/getStyleQuery';
import uniqueArray from '../../../utils/tools/uniqueArray';
import { type Modules } from '../../_definition/types';
import type { FahrzeugsucheFilterForm } from '../types';
import type { FilterMap } from './maps/filterMap';
import getFilterElement from './utils/getFilterElement';

type FahrzeugSucheFilterTopAreaProps = {
    canReset: boolean;
    filters: FahrzeugsucheFilterForm;
    filterElements: FilterMap;
    loading: boolean;
    module: Modules;
    results: UseCarSearchReturn['results'];
    onFilters: UseFormSetValue<FahrzeugsucheFilterForm>;
    onResetFilters: VoidFunction;
};

export function FahrzeugSucheFilterTopArea(props: FahrzeugSucheFilterTopAreaProps) {
    const { filters, filterElements, loading, module, results, canReset, onFilters, onResetFilters } = props;
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const theme = useTheme();
    const { translate } = useLocales();
    const md = useResponsive(rootContainer, 'down', 'lg');
    const sm = useResponsive(rootContainer, 'down', 'md');
    const xs = useResponsive(rootContainer, 'down', 'sm');
    const xsvp = useMediaQuery(theme?.breakpoints?.down('sm'));
    const [advanced, setAdvanced] = useState<boolean>(false);

    const advancedSearchButton = (
        <Button
            variant="outlined"
            color="primary"
            startIcon={<Iconify icon={advanced ? 'mdi:close' : 'mdi:plus'} />}
            onClick={() => setAdvanced(!advanced)}
            sx={{ [getStyleQuery()]: { height: 'fit-content' } }}
        >
            {translate('common:buttons.advancedSearch')}
        </Button>
    );

    const filterResetBtn = (
        <Button color="error" onClick={onResetFilters} startIcon={<Iconify icon="solar:trash-bin-trash-bold" />} sx={{ [getStyleQuery()]: { height: 'fit-content' } }} variant="outlined">
            {translate('common:buttons.clear')}
        </Button>
    );

    const numFound = (
        <Typography component="p" variant="subtitle1" sx={{ flexShrink: 0, fontWeight: 'bold !important', textAlign: { xs: 'center', sm: 'left' } }}>
            {loading ? (
                <Skeleton variant="text" width={canReset && !sm ? 225 : 150} />
            ) : (
                `${results?.numFound || 0} ${(translate(`common:carSearch.foundCar${results?.numFound === 1 ? '' : 's'}`) as string)?.replace('{0}', '')} ${canReset && !md ? `${translate('common:carSearch.forFilters')}:` : ''}`
            )}
        </Typography>
    );

    const topbarFilters = (
        !settings?.fahrzeugsuche?.filter?.top?.includes('[SM]tags') && !settings?.fahrzeugsuche?.filter?.top?.includes('[LG]tags') && !settings?.fahrzeugsuche?.filter?.top?.includes('tags')
            ? uniqueArray<ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther>(['[SM]tags', ...(settings?.fahrzeugsuche?.filter?.top || [])])
            : uniqueArray<ConfigFilterPrefixes<ConfigFilter> | ConfigFilterOther>(settings?.fahrzeugsuche?.filter?.top)
    )?.filter((key) => {
        const f = key.replace('[SM]', '').replace('[LG]', '') as ConfigFilter;
        const filterElement = filterElements?.[f];
        return checkUndefined(settings?.fahrzeugsuche?.forcedFilter?.[filterElement?.filterKey]) && (f !== 'tags' || !checkUndefinedNullOrEmpty(results?.facets?.[filterElement?.facetKey]));
    });

    const indexedArray = uniqueArray(topbarFilters?.map((item, index) => ({ index, value: item })));

    const presortedFilters = indexedArray
        ?.sort((next, curr) => {
            const currWithoutPrefix = curr?.value?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
            const nextWithoutPrefix = next?.value?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
            const nextIndex = next?.index;
            const currType = filterElements?.[currWithoutPrefix]?.type;
            const nextType = filterElements?.[nextWithoutPrefix]?.type;
            if (currType === 'range' && nextType !== 'range') {
                return -1;
            } else if (currType !== 'range' && nextType === 'range' && nextIndex % 2 === 0) {
                return -1;
            } else {
                return 0;
            }
        })
        ?.map((f) => f?.value);

    if (presortedFilters?.filter((k) => k?.startsWith('[SM]'))?.length < 2 || presortedFilters?.filter((k) => k?.startsWith('[LG]'))?.length < 2) {
        presortedFilters[0] = `[SM]${presortedFilters[0].replace('[SM]', '').replace('[LG]', '') as ConfigFilter}`;
        presortedFilters[1] = `[SM]${presortedFilters[1].replace('[SM]', '').replace('[LG]', '') as ConfigFilter}`;
        presortedFilters[2] = `[LG]${presortedFilters[2].replace('[SM]', '').replace('[LG]', '') as ConfigFilter}`;
        presortedFilters[3] = `[LG]${presortedFilters[3].replace('[SM]', '').replace('[LG]', '') as ConfigFilter}`;
    }

    const sortedFilters = {
        direct: presortedFilters
            ?.filter((key) => (xsvp && key.startsWith('[SM]')) || (!xsvp && key.startsWith('[SM]'))) // dont show anymore, but show in collapsed: || key.startsWith('[LG]')
            ?.map((key) => {
                const keyWithoutPrefix = key?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
                return { filterKey: key, filterType: filterElements?.[keyWithoutPrefix]?.type, element: getFilterElement(key, results, settings, translate, xs, loading) };
            })
            ?.filter((f) => !checkUndefinedNullOrEmpty(f?.element)),
        collapse: presortedFilters
            ?.filter((key) => !((xsvp && key.startsWith('[SM]')) || (!xsvp && key.startsWith('[SM]')))) // || key.startsWith('[LG]')
            ?.map((key) => {
                const keyWithoutPrefix = key?.replace(/\[[A-Z]+\]/g, '') as ConfigFilter;
                return { filterKey: key, filterType: filterElements?.[keyWithoutPrefix]?.type, element: getFilterElement(key, results, settings, translate, xs, loading) };
            })
            ?.filter((f) => !checkUndefinedNullOrEmpty(f?.element)),
    };

    const directFilters = useMemo<Record<'nonRange' | 'range', any[]>>(() => ({
        nonRange: sortedFilters?.direct?.filter((f) => f?.filterType !== 'range'),
        range: sortedFilters?.direct?.filter((f) => f?.filterType === 'range'),
    }), [sortedFilters?.direct]);

    const collapseFilters = useMemo<Record<'nonRange' | 'range', any[]>>(() => ({
        nonRange: sortedFilters?.collapse?.filter((f) => f?.filterType !== 'range'),
        range: sortedFilters?.collapse?.filter((f) => f?.filterType === 'range'),
    }), [sortedFilters?.collapse]);

    return (
        <Stack data-testid="FahrzeugSucheFilterTopArea_Container" spacing={3} justifyContent="space-between">
            <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                <Grid size={{ xs: 10, lg: 12 }}>
                    <RHFTextField
                        ariaLabel={translate('common:filter.searchText')}
                        data-testid="freier_text"
                        disabled={settings?.general?.mockup}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" />
                                </InputAdornment>
                            ),
                        }}
                        name="freier_text"
                        placeholder={translate('common:filter.volltext')}
                    />
                </Grid>
                {md && (
                    <Grid size="auto">
                        <CarListSort filters={filters} module={module} onFilters={onFilters} />
                    </Grid>
                )}
            </Grid>

            <Divider />

            <Stack gap={2}>
                <Grid container spacing={2}>
                    {directFilters?.nonRange?.map((f) => (
                        <Grid key={`direct_nonRange_${f?.filterKey}`} size={6}>
                            {f?.element}
                        </Grid>
                    ))}
                    {directFilters?.range?.map((f) => (
                        <Grid key={`direct_range_${f?.filterKey}`} size={{ xs: 12, sm: 6 }}>
                            {f?.element}
                        </Grid>
                    ))}
                </Grid>
                <Collapse in={advanced} unmountOnExit>
                    <Grid container spacing={2}>
                        {collapseFilters?.nonRange?.map((f, i, a) => (
                            <Fragment key={`collapse_nonRange_${f?.filterKey}`}>
                                <Grid size={6}>
                                    {f?.element}
                                </Grid>
                                {(i === (a?.length - 1)) && (i % 2 === 0) && <Grid size={6} />}
                            </Fragment>
                        ))}
                        {collapseFilters?.range?.map((f) => (
                            <Grid key={`collapse_range_${f?.filterKey}`} size={{ xs: 12, sm: 6 }}>
                                {f?.element}
                            </Grid>
                        ))}
                    </Grid>
                </Collapse>

                {xs && advancedSearchButton}

                {md && (
                    <Box mt={1}>
                        <ChipFilters canReset={canReset} facets={results?.facets} filterElements={filterElements} filters={filters} onFilters={onFilters} onResetFilters={onResetFilters} />
                    </Box>
                )}

                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns={{
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                    }}
                    alignItems="center"
                    gridTemplateRows="repeat(1, 1fr)"
                    sx={{ maxHeight: '100%', overflow: 'visible' }}
                >
                    {!xs && advancedSearchButton}

                    {md && (
                        <Box justifySelf="flex-start" mt={xs ? -1 : undefined}>
                            {numFound}
                        </Box>
                    )}

                    {canReset && !md && filterResetBtn}
                </Box>
            </Stack>

            <Divider />
        </Stack>
    );
}
